import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';
import CalendarBulkImport from './CalendarBulkImport';
import CalendarSubscribers from './CalendarSubscribers';
import { countyLabel } from '../../utils/countyDisplay';

/**
 * Master admin: Auction Calendar.
 *
 * Platform level reference data, not tenant data. Everything here reads and
 * writes /api/master-admin/calendar, which is mounted behind
 * authenticateMasterAdmin.
 *
 * The product is sold as "maintained by hand, updated monthly" across roughly
 * 900 counties, so entry speed is the margin. That shapes the whole screen:
 * inline editing rather than modals, Enter to save, Escape to cancel, and the
 * search box focused on arrival.
 *
 * The Bulk import tab is the other half of that. Inline editing is for one row
 * the owner is looking at, and bulk paste is for the spreadsheet a research
 * session produced. Sales from either path go through lib/calendarWrites, so
 * the history log cannot miss one. What tells the two apart afterwards is the
 * feeder column, which bulk paste stamps and hand entry leaves empty.
 *
 * Every colour is a theme token, so the page follows the system, light and dark
 * settings without a second stylesheet.
 */

const SALE_TYPES = [
  { value: '', label: 'Not set' },
  { value: 'lien', label: 'Lien' },
  { value: 'deed', label: 'Deed' },
  { value: 'redeemable_deed', label: 'Redeemable deed' },
  { value: 'hybrid', label: 'Hybrid' }
];

const SALE_RUNNERS = [
  { value: '', label: 'Not set' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'clerk_and_master', label: 'Clerk and Master' },
  { value: 'chancery_clerk', label: 'Chancery Clerk' },
  { value: 'tax_collector', label: 'Tax Collector' },
  { value: 'county_clerk', label: 'County Clerk' },
  { value: 'other', label: 'Other' }
];

const SALE_KINDS = [
  { value: 'annual_tax_sale', label: 'Annual tax sale' },
  { value: 'resale', label: 'Resale' },
  { value: 'otc_list', label: 'Over the counter list' },
  { value: 'state_held', label: 'State held' },
  { value: 'surplus', label: 'Surplus' },
  { value: 'other', label: 'Other' }
];

const PLATFORMS = [
  { value: '', label: 'Not set' },
  { value: 'in_person', label: 'In person' },
  { value: 'sealed_bid', label: 'Sealed bid' },
  { value: 'govease', label: 'GovEase' },
  { value: 'realauction', label: 'RealAuction' },
  { value: 'bid4assets', label: 'Bid4Assets' },
  { value: 'wisconsin_surplus', label: 'Wisconsin Surplus' },
  { value: 'public_surplus', label: 'Public Surplus' },
  { value: 'cosl_online', label: 'COSL online' },
  { value: 'county_site', label: 'County site' },
  { value: 'other', label: 'Other' }
];

const SALE_STATUSES = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'expected', label: 'Expected' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'cancelled', label: 'Cancelled' }
];

const MONTHS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_BADGE = {
  confirmed: 'badge-success',
  expected: 'badge-warning',
  unknown: 'badge-secondary',
  cancelled: 'badge-danger'
};

const EMPTY_SALE = {
  sale_kind: 'annual_tax_sale',
  platform: '',
  typical_month: '',
  next_sale_date: '',
  registration_deadline: '',
  list_url: '',
  status: 'expected',
  source_url: '',
  notes: ''
};

/** A date column from pg arrives as an ISO timestamp. Forms want YYYY-MM-DD. */
function toDateInput(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function labelFor(options, value) {
  const found = options.find(o => o.value === (value || ''));
  return found ? found.label : value;
}

/**
 * Why a state is not fit to be Live, in the words the owner is asked with.
 *
 * THE SAME FUNCTION EXISTS ON THE SERVER, in routes/masterAdminCalendarRoutes.js,
 * and it is the one that decides: this copy only decides whether to ask before
 * sending. A browser can be bypassed, so the rule cannot live here alone, and a
 * rule enforced in two places has to be enforced in the same words or the
 * question and the refusal stop describing the same thing. check:calendar runs
 * both copies over the same three cases and compares the sentences.
 *
 * Ready is verified, plus at least one county carrying a sale with a date or a
 * typical month. A sale with neither is not shown to a subscriber at all, so a
 * state made only of those is empty from where they are sitting.
 *
 * There is no coverage threshold on purpose. One county out of two hundred
 * passes. How much is enough is the owner's call, and the readiness line prints
 * both numbers so it can be made on sight.
 */
function liveReadinessProblem(name, verified, countiesWithSales) {
  const hasSales = Number(countiesWithSales) > 0;
  if (verified && hasSales) return null;
  if (verified) {
    return `${name} is verified but has no sales loaded. Subscribers will see an empty state.`;
  }
  if (hasSales) {
    return `${name} has sales loaded but has not been verified. `
      + 'Subscribers will see information nobody has checked.';
  }
  return `${name} is unverified and has no sales loaded. Subscribers will see an empty state.`;
}

/**
 * The one line under a state's name that says how far along it is.
 *
 * Read entirely from the row the list query already returned. Asking the server
 * per row would be fifty requests to render one table, and the counts are
 * cheap where they are: the list query was already joining counties and sales
 * to count them.
 */
function readinessLine(state) {
  const total = Number(state.county_count || 0);
  const ready = Number(state.counties_with_sales || 0);
  const stamp = state.verified_at ? 'Verified' : 'Unverified';
  return `${stamp}, ${ready} of ${total} counties have a sale with a date or a month`;
}

/**
 * The outcome of a save, shown under that form's own buttons.
 *
 * The banner at the top of the page is still written to, but it is the wrong
 * and only place for this: the counties list runs to hundreds of rows, so an
 * owner editing one near the bottom pressed Save, saw nothing move, and had no
 * way to tell a refusal from a slow request. This sits where they are looking.
 *
 * Both themes are covered by the shared variables rather than fixed colours,
 * because the master admin screens carry a theme toggle.
 */
function FormMessage({ message, innerRef }) {
  if (!message) return null;

  const isError = message.kind === 'error';
  return (
    <div
      ref={innerRef}
      role={isError ? 'alert' : 'status'}
      style={{
        marginTop: '10px',
        padding: '8px 10px',
        borderRadius: '4px',
        fontSize: '0.9rem',
        color: isError ? 'var(--color-error-dark)' : 'var(--color-success-dark)',
        background: isError ? 'var(--color-error-light)' : 'var(--color-success-light)',
        border: `1px solid ${isError ? 'var(--color-error)' : 'var(--color-success)'}`
      }}
    >
      {message.text}
    </div>
  );
}

/** How long a row keeps its Saved flag before it clears itself. */
const SAVED_FLAG_MS = 2500;

/**
 * Off screen but still read aloud. There is no such utility class in the
 * stylesheet, and one inline object shared by the flags is cheaper than adding
 * a global class for three call sites.
 */
const srOnlyStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0
};

/**
 * The confirmation that a row saved, shown on the row itself.
 *
 * The edit panels close on a save that worked, so the only feedback left was
 * the banner at the top of the page, which is off screen on a list of fifty
 * states or hundreds of counties. That is the complaint this answers, and it
 * is also what makes a save look different from a cancel: both close the
 * panel, only one leaves this behind.
 *
 * Two things are load bearing here.
 *
 * The outer span is always rendered, empty when there is nothing to say. It
 * reserves its own width so the buttons beside it never move, and it is the
 * live region: a region that only appears at the moment its content does is
 * unreliably announced, because assistive technology has to be watching it
 * beforehand.
 *
 * The visible pill is hidden from that announcement and a fuller sentence is
 * read instead, so the row is named rather than a bare "Saved" with nothing
 * to attach it to.
 */
function RowSaveFlag({ show, label }) {
  return (
    <span
      role="status"
      aria-live="polite"
      style={{
        display: 'inline-block',
        minWidth: '58px',
        marginLeft: '6px',
        textAlign: 'center',
        verticalAlign: 'middle'
      }}
    >
      {show && (
        <>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--color-success-dark)',
              background: 'var(--color-success-light)',
              border: '1px solid var(--color-success)'
            }}
          >
            Saved
          </span>
          <span style={srOnlyStyle}>{label} saved</span>
        </>
      )}
    </span>
  );
}

function MasterAdminCalendar() {
  const navigate = useNavigate();
  const token = localStorage.getItem('masterAdminToken');

  const [view, setView] = useState('states');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [counties, setCounties] = useState([]);
  const [countySearch, setCountySearch] = useState('');
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  const [editingState, setEditingState] = useState(null);
  const [editingCounty, setEditingCounty] = useState(null);
  const [stateDraft, setStateDraft] = useState({});
  const [countyDraft, setCountyDraft] = useState({});

  // The row_version each edit form was opened against, sent back on save so the
  // server can refuse a write that would land on top of a newer one.
  //
  // This is the server's own canonical string and is echoed back untouched. It
  // used to be updated_at, which arrived as a Date, lost its sub-millisecond
  // digits on the way through JSON, and so never matched what was stored: every
  // save was refused as stale. See the note above staleGuard in
  // server/routes/masterAdminCalendarRoutes.js.
  const [stateLoadedAt, setStateLoadedAt] = useState(null);
  const [countyLoadedAt, setCountyLoadedAt] = useState(null);

  // Save feedback that belongs beside the form rather than only in the banner at
  // the top of the page. A refused save is about the row under the cursor, and
  // the owner editing a county far down the list never saw the banner at all.
  // Shape: { kind: 'error' | 'ok', text }.
  const [savingState, setSavingState] = useState(false);
  const [savingCounty, setSavingCounty] = useState(false);
  const [savingSale, setSavingSale] = useState(false);
  const [stateFormMessage, setStateFormMessage] = useState(null);
  const [countyFormMessage, setCountyFormMessage] = useState(null);
  const [saleFormMessage, setSaleFormMessage] = useState(null);
  const stateMessageRef = useRef(null);
  const countyMessageRef = useRef(null);
  const saleMessageRef = useRef(null);

  // The Live question, open over the state edit panel it was asked from.
  // Shape: { code, problem }. Null the rest of the time, which is most of it:
  // a state that is ready goes Live without being asked anything.
  const [liveConfirm, setLiveConfirm] = useState(null);
  const liveConfirmRef = useRef(null);

  // A state code the import result asked to be shown. Cleared once the row has
  // been scrolled to, so returning to the tab later does not jump again.
  const [focusStateCode, setFocusStateCode] = useState('');
  const stateRowRefs = useRef({});

  // Which rows are currently showing their Saved flag, keyed by list and id so
  // county 7 and sale 7 cannot mark each other. One timer per key, held in a
  // ref because a timer id is not something the render cares about.
  const [savedFlags, setSavedFlags] = useState({});
  const savedTimers = useRef({});

  const clearSavedFlag = useCallback((key) => {
    const timers = savedTimers.current;
    if (timers[key]) {
      window.clearTimeout(timers[key]);
      delete timers[key];
    }
    setSavedFlags(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const markSaved = useCallback((key) => {
    const timers = savedTimers.current;
    // A second save of the same row restarts its window rather than inheriting
    // the tail of the first one.
    if (timers[key]) window.clearTimeout(timers[key]);
    setSavedFlags(prev => ({ ...prev, [key]: true }));
    timers[key] = window.setTimeout(() => {
      delete timers[key];
      setSavedFlags(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, SAVED_FLAG_MS);
  }, []);

  // Unmounting with timers outstanding would leave them to fire against a gone
  // component. Leaving the tab is the ordinary way that happens.
  useEffect(() => {
    const timers = savedTimers.current;
    return () => {
      Object.values(timers).forEach(window.clearTimeout);
    };
  }, []);

  const [expandedCounty, setExpandedCounty] = useState(null);
  const [sales, setSales] = useState([]);
  const [editingSale, setEditingSale] = useState(null);
  const [saleDraft, setSaleDraft] = useState(EMPTY_SALE);
  const [addingSale, setAddingSale] = useState(false);
  const [history, setHistory] = useState({ saleId: null, rows: [] });

  const [attention, setAttention] = useState(null);
  const [attentionState, setAttentionState] = useState('');
  // The id currently being resolved, so one row's button says what it is doing
  // without disabling the rest of the list.
  const [resolving, setResolving] = useState(null);

  const searchRef = useRef(null);

  const api = useCallback(async (path, options = {}) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/calendar${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {})
      }
    });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('masterAdminToken');
      navigate('/master-admin/login');
      return null;
    }

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  }, [token, navigate]);

  const flash = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2500);
  };

  // A refused save has to be read, so it is scrolled to rather than left to be
  // found. Success is not scrolled to: the owner is already looking at the form
  // they just saved, and yanking the page under them would be worse than silent.
  useEffect(() => {
    if (stateFormMessage?.kind === 'error' && stateMessageRef.current) {
      stateMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [stateFormMessage]);

  useEffect(() => {
    if (countyFormMessage?.kind === 'error' && countyMessageRef.current) {
      countyMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [countyFormMessage]);

  // The sale form had the same defect the county panel had, and worse: it
  // closed on a refusal, so the typed row was gone as well as unexplained.
  useEffect(() => {
    if (saleFormMessage?.kind === 'error' && saleMessageRef.current) {
      saleMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [saleFormMessage]);

  // A question the owner has to answer is no use behind them on the page, and
  // no use to a screen reader that is not looking at it, so focus goes to it.
  useEffect(() => {
    if (liveConfirm && liveConfirmRef.current) {
      liveConfirmRef.current.focus();
      liveConfirmRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [liveConfirm]);

  // Brings the row an import result linked to into view, and puts focus on it
  // so the keyboard lands there too.
  useEffect(() => {
    if (view !== 'states' || !focusStateCode) return;
    const row = stateRowRefs.current[focusStateCode];
    if (!row) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.focus();
    setFocusStateCode('');
  }, [view, focusStateCode, states]);

  const loadStates = useCallback(async () => {
    try {
      const data = await api('/states');
      if (data) setStates(data.states);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadCounties = useCallback(async () => {
    if (!selectedState) {
      setCounties([]);
      return;
    }
    try {
      const params = new URLSearchParams({ state: selectedState });
      if (countySearch) params.append('search', countySearch);
      if (unverifiedOnly) params.append('unverified', 'true');
      const data = await api(`/counties?${params}`);
      if (data) setCounties(data.counties);
    } catch (err) {
      setError(err.message);
    }
  }, [api, selectedState, countySearch, unverifiedOnly]);

  const loadAttention = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (attentionState) params.append('state', attentionState);
      const data = await api(`/needs-attention?${params}`);
      if (data) setAttention(data);
    } catch (err) {
      setError(err.message);
    }
  }, [api, attentionState]);

  /**
   * Reload everything a bulk import can have written, awaited by the import tab
   * before it renders its result table.
   *
   * The owner hit the reason this exists during the first real import: after
   * importing the Wisconsin summaries, the States tab still held the rows it
   * had loaded beforehand, so opening Edit showed empty fields and saving would
   * have written blanks over the imported text.
   */
  const reloadAfterImport = useCallback(async () => {
    const jobs = [loadStates(), loadCounties()];

    // An expanded county is showing sales that the import may have just moved.
    if (expandedCounty) {
      jobs.push((async () => {
        try {
          const data = await api(`/counties/${expandedCounty}/sales`);
          if (data) setSales(data.sales);
        } catch (err) {
          setError(err.message);
        }
      })());
    }

    await Promise.all(jobs);
  }, [api, loadStates, loadCounties, expandedCounty]);

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    loadStates();
  }, [token, navigate, loadStates]);

  useEffect(() => {
    loadCounties();
  }, [loadCounties]);

  /**
   * Take a report off the list.
   *
   * Reloads rather than splicing the row out locally, because the same call
   * refreshes the count behind the tab badge, and a badge that disagrees with
   * the list under it is worse than a slightly slower button.
   *
   * alreadyResolved is a normal answer, not an error: two tabs open on the same
   * backlog is the ordinary case. Either way the row goes.
   */
  const resolveReport = useCallback(async (id) => {
    setResolving(id);
    try {
      const data = await api(`/problem-reports/${id}/resolve`, { method: 'POST' });
      if (data) {
        await loadAttention();
        setNotice(data.alreadyResolved
          ? 'That report had already been resolved.'
          : 'Report resolved.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setResolving(null);
    }
  }, [api, loadAttention]);

  // Loaded on arrival, not only when the tab is opened, because the badge on
  // the tab is the whole point: a report nobody has looked at has to be visible
  // from the tab the owner is already on. Reloaded on every view change so the
  // count is right after resolving one, and cheap enough to do that way.
  useEffect(() => {
    loadAttention();
  }, [loadAttention]);

  // The owner arrives to type. Focus the search as soon as a state is chosen.
  useEffect(() => {
    if (view === 'counties' && selectedState && searchRef.current) {
      searchRef.current.focus();
    }
  }, [view, selectedState]);

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  // ---------------------------------------------------------------- states

  const startEditState = async (code) => {
    try {
      // Always from the API, never from the row already in `states`. That list
      // can predate a bulk import, and a form filled from it would offer the
      // owner a screen of blanks to save over freshly imported text.
      const data = await api(`/states/${code}`);
      if (!data) return;
      const state = data.state;

      setStates(prev => prev.map(s => (s.state_code === code ? { ...s, ...state } : s)));
      setEditingState(code);
      setStateFormMessage(null);
      setLiveConfirm(null);
      setStateLoadedAt(state.row_version || null);
      setStateDraft({
        sale_type: state.sale_type || '',
        redemption_summary: state.redemption_summary || '',
        interest_or_penalty_summary: state.interest_or_penalty_summary || '',
        otc_route_summary: state.otc_route_summary || '',
        primary_statute_citation: state.primary_statute_citation || '',
        notes: state.notes || '',
        in_scope: state.in_scope
      });
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Save the state edit panel.
   *
   * `confirmNotReady` is the owner's answer to the Live question, and it is
   * only ever true on the second press: the first press asks, this one goes
   * ahead. The browser decides when to ask, never whether the write is allowed,
   * which is the server's call and stays the server's call.
   */
  const saveState = async (code, { confirmNotReady = false } = {}) => {
    if (savingState) return;

    // Ask before sending when this would turn a thin state Live. The same rule
    // runs on the server, which answers 409 for anything that arrives without
    // the flag, so this is the polite path and not the guard itself.
    if (!confirmNotReady && stateDraft.in_scope === true) {
      const row = states.find(s => s.state_code === code);
      const wasLive = Boolean(row && row.in_scope);
      const problem = row
        ? liveReadinessProblem(row.name, Boolean(row.verified_at), row.counties_with_sales)
        : null;
      if (!wasLive && problem) {
        setLiveConfirm({ code, problem });
        return;
      }
    }

    setLiveConfirm(null);
    setSavingState(true);
    setStateFormMessage(null);
    // A save that is starting makes the flag from the previous one stale.
    clearSavedFlag(`state:${code}`);
    try {
      // if_row_version closes the window between opening this form and saving
      // it. The server refuses the write with a 409 if the row moved.
      const data = await api(`/states/${code}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...stateDraft,
          if_row_version: stateLoadedAt,
          ...(confirmNotReady ? { confirm_not_ready: true } : {})
        })
      });
      if (!data) return;
      setStates(prev => prev.map(s => (s.state_code === code ? { ...s, ...data.state } : s)));
      // The row moved because this save moved it, so the version this component
      // holds is kept level with the server rather than left stale.
      setStateLoadedAt(data.state.row_version || null);
      // A save that worked is finished, so the panel goes away and the row
      // underneath it shows what was just written. The confirmation is the
      // page level flash, not a message inside a panel that is closing.
      //
      // Only on success. The catch below leaves the panel open with the typed
      // values untouched, because a refused save is the one case where the
      // owner still needs the form.
      setStateFormMessage(null);
      setEditingState(null);
      markSaved(`state:${code}`);
      flash(`${code} saved`);
    } catch (err) {
      setStateFormMessage({ kind: 'error', text: err.message });
      setError(err.message);
    } finally {
      setSavingState(false);
    }
  };

  const stampState = async (code, clear) => {
    try {
      const data = await api(`/states/${code}/verify`, {
        method: 'POST',
        body: JSON.stringify({ clear: Boolean(clear) })
      });
      if (!data) return;
      setStates(prev => prev.map(s => (s.state_code === code ? { ...s, ...data.state } : s)));
      flash(clear ? `${code} marked unverified` : `${code} verified`);
    } catch (err) {
      setError(err.message);
    }
  };

  // -------------------------------------------------------------- counties

  const startEditCounty = async (id) => {
    try {
      // Fresh from the API for the same reason as the state form above.
      const data = await api(`/counties/${id}`);
      if (!data) return;
      const county = data.county;

      setCounties(prev => prev.map(c => (c.id === id ? { ...c, ...county } : c)));
      setEditingCounty(id);
      setCountyFormMessage(null);
      setCountyLoadedAt(county.row_version || null);
      setCountyDraft({
        county_name: county.county_name || '',
        county_seat: county.county_seat || '',
        sale_runner: county.sale_runner || '',
        sale_website_url: county.sale_website_url || '',
        contact_phone: county.contact_phone || '',
        contact_email: county.contact_email || '',
        notes: county.notes || ''
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const saveCounty = async (id) => {
    if (savingCounty) return;
    setSavingCounty(true);
    setCountyFormMessage(null);
    clearSavedFlag(`county:${id}`);
    try {
      const data = await api(`/counties/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...countyDraft, if_row_version: countyLoadedAt })
      });
      if (!data) return;
      setCounties(prev => prev.map(c => (c.id === id ? { ...c, ...data.county } : c)));
      // Same reason as the state save above: the version this component holds
      // is kept level with the server rather than left stale.
      setCountyLoadedAt(data.county.row_version || null);
      // Closes on success, confirms on the row, exactly as the state panel
      // does. The catch below leaves the panel open with the typed values
      // untouched, because a refused save is when the owner still needs it.
      setCountyFormMessage(null);
      setEditingCounty(null);
      markSaved(`county:${id}`);
      flash('County saved');
    } catch (err) {
      setCountyFormMessage({ kind: 'error', text: err.message });
      setError(err.message);
    } finally {
      setSavingCounty(false);
    }
  };

  const stampCounty = async (id, clear) => {
    try {
      const data = await api(`/counties/${id}/verify`, {
        method: 'POST',
        body: JSON.stringify({ clear: Boolean(clear) })
      });
      if (!data) return;
      setCounties(prev => prev.map(c => (c.id === id ? { ...c, ...data.county } : c)));
      flash(clear ? 'Marked unverified' : 'Verified');
    } catch (err) {
      setError(err.message);
    }
  };

  // ----------------------------------------------------------------- sales

  const openCounty = async (county) => {
    if (expandedCounty === county.id) {
      setExpandedCounty(null);
      setSales([]);
      return;
    }
    setExpandedCounty(county.id);
    setAddingSale(false);
    setEditingSale(null);
    setHistory({ saleId: null, rows: [] });
    try {
      const data = await api(`/counties/${county.id}/sales`);
      if (data) setSales(data.sales);
    } catch (err) {
      setError(err.message);
    }
  };

  const startAddSale = () => {
    setAddingSale(true);
    setEditingSale(null);
    setSaleFormMessage(null);
    setSaleDraft({ ...EMPTY_SALE });
  };

  const startEditSale = (sale) => {
    setEditingSale(sale.id);
    setAddingSale(false);
    setSaleFormMessage(null);
    setSaleDraft({
      sale_kind: sale.sale_kind || 'annual_tax_sale',
      platform: sale.platform || '',
      typical_month: sale.typical_month || '',
      next_sale_date: toDateInput(sale.next_sale_date),
      registration_deadline: toDateInput(sale.registration_deadline),
      list_url: sale.list_url || '',
      status: sale.status || 'unknown',
      source_url: sale.source_url || '',
      notes: sale.notes || ''
    });
  };

  /**
   * Add a sale.
   *
   * A refusal leaves the form exactly as it was typed, with the reason beside
   * it. Nothing about the draft is touched in the catch, which is the whole
   * point: a sale is eight fields copied off a county website, and losing them
   * to a validation message is losing the work, not just the click.
   */
  const createSale = async () => {
    if (savingSale) return;
    if (!saleDraft.source_url.trim()) {
      const text = 'A source URL is required. Every sale records where its information came from.';
      setSaleFormMessage({ kind: 'error', text });
      setError(text);
      return;
    }
    setSavingSale(true);
    setSaleFormMessage(null);
    try {
      const data = await api(`/counties/${expandedCounty}/sales`, {
        method: 'POST',
        body: JSON.stringify(saleDraft)
      });
      if (!data) return;
      setSales(prev => [...prev, data.sale]);
      setCounties(prev => prev.map(c => (
        c.id === expandedCounty ? { ...c, sale_count: (c.sale_count || 0) + 1 } : c
      )));
      // Straight back into a fresh row: the owner is entering a run of these.
      setSaleDraft({ ...EMPTY_SALE, source_url: saleDraft.source_url });
      flash('Sale added');
    } catch (err) {
      setSaleFormMessage({ kind: 'error', text: err.message });
      setError(err.message);
    } finally {
      setSavingSale(false);
    }
  };

  /**
   * Save an edited sale.
   *
   * ON SUCCESS the form closes and the row it saved carries the flag, which is
   * what the state and county panels do and what this already did.
   *
   * ON A REFUSAL it stays open. It used to close anyway and send the reason to
   * the banner at the top of the page, so the owner lost the typed row and had
   * to hunt for the explanation. Nothing in the catch touches the draft, the
   * open form or the row flag.
   */
  const saveSale = async (id) => {
    if (savingSale) return;
    setSavingSale(true);
    setSaleFormMessage(null);
    clearSavedFlag(`sale:${id}`);
    try {
      const data = await api(`/sales/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(saleDraft)
      });
      if (!data) return;
      setSales(prev => prev.map(s => (s.id === id ? data.sale : s)));
      setEditingSale(null);
      markSaved(`sale:${id}`);
      flash(data.changed.length === 0
        ? 'No changes to save'
        : `Saved, ${data.changed.length} field${data.changed.length === 1 ? '' : 's'} logged`);
    } catch (err) {
      setSaleFormMessage({ kind: 'error', text: err.message });
      setError(err.message);
    } finally {
      setSavingSale(false);
    }
  };

  const loadHistory = async (saleId) => {
    if (history.saleId === saleId) {
      setHistory({ saleId: null, rows: [] });
      return;
    }
    try {
      const data = await api(`/sales/${saleId}/history`);
      if (data) setHistory({ saleId, rows: data.history });
    } catch (err) {
      setError(err.message);
    }
  };

  /** Enter saves, Escape cancels. Not bound on textareas, where Enter is text. */
  const keyHandler = (onSave, onCancel) => (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    background: 'var(--color-input-bg)',
    color: 'var(--color-input-text)',
    border: '1px solid var(--color-input-border)',
    borderRadius: '4px',
    fontSize: '0.9rem'
  };

  const cellLabelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    color: 'var(--color-text-muted)',
    marginBottom: '2px'
  };

  const panelStyle = {
    background: 'var(--color-surface-sunken)',
    border: '1px solid var(--color-border)',
    borderRadius: '6px',
    padding: '16px',
    marginTop: '8px'
  };

  const liveStates = states.filter(s => s.in_scope);

  // The server counts these, so the badge and the list cannot disagree about
  // what open means. Falls back to the list's own length while the first load
  // is still in flight.
  const openReports = attention
    ? (attention.openProblemReports ?? (attention.problemReports || []).length)
    : 0;

  return (
    <div className="master-admin-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Auction Calendar</h1>
        </div>
        <div className="admin-header-right">
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary">Log Out</button>
        </div>
      </header>

      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link active">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
        <Link to="/master-admin/settings" className="admin-nav-link">Settings</Link>
      </nav>

      <div className="admin-content" style={{ padding: '20px' }}>
        {error && (
          <div className="card card-error" style={{ marginBottom: '16px' }}>
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="btn btn-secondary"
              style={{ marginLeft: '12px' }}
            >
              Dismiss
            </button>
          </div>
        )}
        {notice && (
          <div className="card card-success" style={{ marginBottom: '16px' }}>{notice}</div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            className={`btn ${view === 'states' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('states')}
          >
            States
          </button>
          <button
            className={`btn ${view === 'counties' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('counties')}
          >
            Counties and sales
          </button>
          <button
            className={`btn ${view === 'attention' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('attention')}
          >
            Needs attention
            {/* Only problem reports, not the stale dates beside them. A person
                is waiting on the other end of one of these, and a count that
                also included every county with a soft date would never be
                zero, which is the same as having no badge at all. */}
            {openReports > 0 && (
              <span
                className="badge badge-warning"
                style={{ marginLeft: '8px' }}
                aria-label={`${openReports} open problem report${openReports === 1 ? '' : 's'}`}
              >
                {openReports}
              </span>
            )}
          </button>
          <button
            className={`btn ${view === 'bulk' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('bulk')}
          >
            Bulk import
          </button>
          <button
            className={`btn ${view === 'subscribers' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('subscribers')}
          >
            Subscribers
          </button>
        </div>

        {loading && <p style={{ color: 'var(--color-text-light)' }}>Loading the calendar.</p>}

        {/* ------------------------------------------------------ states */}
        {!loading && view === 'states' && (
          <div className="card">
            <p style={{ color: 'var(--color-text-light)', marginBottom: '12px' }}>
              All fifty states are seeded so that making one live is a single
              change rather than a migration. The {liveStates.length} live states are listed first.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Status</th>
                    <th>Ready for Live</th>
                    <th>Sale type</th>
                    <th>Counties</th>
                    <th>Verified counties</th>
                    <th>Sales</th>
                    <th>Stamp</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {states.map(state => (
                    <React.Fragment key={state.state_code}>
                      {/* Focusable so an import result can send the keyboard
                          here, not only the scroll position. */}
                      <tr
                        tabIndex={-1}
                        ref={el => { stateRowRefs.current[state.state_code] = el; }}
                      >
                        <td style={{ fontWeight: 600 }}>
                          {state.state_code} {state.name}
                        </td>
                        <td>
                          <span className={`badge ${state.in_scope ? 'badge-primary' : 'badge-secondary'}`}>
                            {state.in_scope ? 'Live' : 'Coming soon'}
                          </span>
                        </td>
                        {/* Read from the row the list query already returned.
                            One sentence, both numbers, no request per row. */}
                        <td style={{ color: 'var(--color-text-light)', fontSize: '0.85rem', minWidth: '210px' }}>
                          {readinessLine(state)}
                        </td>
                        <td>{state.sale_type ? labelFor(SALE_TYPES, state.sale_type) : 'Not set'}</td>
                        <td>{state.county_count}</td>
                        <td>{state.verified_county_count}</td>
                        <td>{state.sale_count}</td>
                        <td>
                          {state.verified_at
                            ? <span className="badge badge-success">{new Date(state.verified_at).toLocaleDateString()}</span>
                            : <span className="badge badge-secondary">Unverified</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-secondary" onClick={() => startEditState(state.state_code)}>Edit</button>
                          <button
                            className="btn btn-secondary"
                            style={{ marginLeft: '6px' }}
                            onClick={() => stampState(state.state_code, Boolean(state.verified_at))}
                          >
                            {state.verified_at ? 'Unverify' : 'Verify'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ marginLeft: '6px' }}
                            onClick={() => { setSelectedState(state.state_code); setView('counties'); }}
                          >
                            Counties
                          </button>
                          <RowSaveFlag
                            show={Boolean(savedFlags[`state:${state.state_code}`])}
                            label={state.name}
                          />
                        </td>
                      </tr>
                      {editingState === state.state_code && (
                        <tr>
                          <td colSpan={9}>
                            <div style={panelStyle}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                                <div>
                                  <label style={cellLabelStyle} htmlFor={`sale-type-${state.state_code}`}>Sale type</label>
                                  <select
                                    id={`sale-type-${state.state_code}`}
                                    style={inputStyle}
                                    value={stateDraft.sale_type}
                                    onChange={e => setStateDraft({ ...stateDraft, sale_type: e.target.value })}
                                  >
                                    {SALE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={cellLabelStyle} htmlFor={`statute-${state.state_code}`}>Primary statute</label>
                                  <input
                                    id={`statute-${state.state_code}`}
                                    style={inputStyle}
                                    value={stateDraft.primary_statute_citation}
                                    onChange={e => setStateDraft({ ...stateDraft, primary_statute_citation: e.target.value })}
                                    onKeyDown={keyHandler(() => saveState(state.state_code), () => setEditingState(null))}
                                  />
                                </div>
                                <div>
                                  <label style={cellLabelStyle} htmlFor={`scope-${state.state_code}`}>Status</label>
                                  <select
                                    id={`scope-${state.state_code}`}
                                    style={inputStyle}
                                    value={stateDraft.in_scope ? 'true' : 'false'}
                                    onChange={e => setStateDraft({ ...stateDraft, in_scope: e.target.value === 'true' })}
                                  >
                                    <option value="true">Live</option>
                                    <option value="false">Coming soon</option>
                                  </select>
                                </div>
                              </div>

                              {[
                                ['redemption_summary', 'Redemption'],
                                ['interest_or_penalty_summary', 'Interest or penalty'],
                                ['otc_route_summary', 'Over the counter route'],
                                ['notes', 'Notes']
                              ].map(([field, label]) => (
                                <div key={field} style={{ marginTop: '12px' }}>
                                  <label style={cellLabelStyle} htmlFor={`${field}-${state.state_code}`}>{label}</label>
                                  <textarea
                                    id={`${field}-${state.state_code}`}
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                    value={stateDraft[field]}
                                    onChange={e => setStateDraft({ ...stateDraft, [field]: e.target.value })}
                                  />
                                </div>
                              ))}

                              {/* The Live question. Inline rather than a
                                  browser confirm box, so the owner can still
                                  see the panel it is about, and focused on
                                  open so the keyboard and a screen reader both
                                  arrive at it. The server asks the same
                                  question of the request itself, so pressing
                                  past this is a decision, not a bypass. */}
                              {liveConfirm && liveConfirm.code === state.state_code && (
                                <div
                                  ref={liveConfirmRef}
                                  tabIndex={-1}
                                  role="alertdialog"
                                  aria-labelledby={`live-confirm-title-${state.state_code}`}
                                  aria-describedby={`live-confirm-body-${state.state_code}`}
                                  style={{
                                    marginTop: '14px',
                                    padding: '12px 14px',
                                    borderRadius: '4px',
                                    color: 'var(--color-error-dark)',
                                    background: 'var(--color-error-light)',
                                    border: '1px solid var(--color-error)'
                                  }}
                                >
                                  <strong id={`live-confirm-title-${state.state_code}`}>
                                    Make {state.name} Live?
                                  </strong>
                                  <p id={`live-confirm-body-${state.state_code}`} style={{ margin: '6px 0 0' }}>
                                    {liveConfirm.problem}
                                  </p>
                                  <div style={{ marginTop: '10px' }}>
                                    <button
                                      className="btn btn-secondary"
                                      onClick={() => setLiveConfirm(null)}
                                    >
                                      Go back
                                    </button>
                                    <button
                                      className="btn btn-primary"
                                      style={{ marginLeft: '8px' }}
                                      onClick={() => saveState(state.state_code, { confirmNotReady: true })}
                                      disabled={savingState}
                                    >
                                      Make it Live anyway
                                    </button>
                                  </div>
                                </div>
                              )}

                              <div style={{ marginTop: '12px' }}>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => saveState(state.state_code)}
                                  disabled={savingState}
                                >
                                  {savingState ? 'Saving' : 'Save'}
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  style={{ marginLeft: '8px' }}
                                  onClick={() => setEditingState(null)}
                                  disabled={savingState}
                                >
                                  Cancel
                                </button>
                                <span style={{ marginLeft: '12px', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                                  Enter saves a single line field. Escape cancels.
                                </span>
                                <FormMessage message={stateFormMessage} innerRef={stateMessageRef} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------- counties */}
        {!loading && view === 'counties' && (
          <div className="card">
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ minWidth: '200px' }}>
                <label style={cellLabelStyle} htmlFor="county-state">State</label>
                <select
                  id="county-state"
                  style={inputStyle}
                  value={selectedState}
                  onChange={e => { setSelectedState(e.target.value); setExpandedCounty(null); }}
                >
                  <option value="">Choose a state</option>
                  {states.map(s => (
                    <option key={s.state_code} value={s.state_code}>
                      {s.state_code} {s.name} ({s.county_count})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ minWidth: '240px', flex: 1 }}>
                <label style={cellLabelStyle} htmlFor="county-search">Search by name or FIPS</label>
                <input
                  id="county-search"
                  ref={searchRef}
                  style={inputStyle}
                  value={countySearch}
                  onChange={e => setCountySearch(e.target.value)}
                  placeholder="Maricopa, or 04013"
                />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-text)' }}>
                  <input
                    type="checkbox"
                    checked={unverifiedOnly}
                    onChange={e => setUnverifiedOnly(e.target.checked)}
                  />
                  Unverified only
                </label>
              </div>
            </div>

            {!selectedState && (
              <p style={{ color: 'var(--color-text-light)' }}>Choose a state to list its counties.</p>
            )}

            {selectedState && (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>County</th>
                      <th>FIPS</th>
                      <th>Seat</th>
                      <th>Runs the sale</th>
                      <th>Sales</th>
                      <th>Stamp</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counties.length === 0 && (
                      <tr><td colSpan={7} style={{ color: 'var(--color-text-light)' }}>No counties match.</td></tr>
                    )}
                    {counties.map(county => (
                      <React.Fragment key={county.id}>
                        <tr>
                          <td style={{ fontWeight: 600 }}>{countyLabel(county.county_name)}</td>
                          <td style={{ fontFamily: 'monospace' }}>{county.fips_code}</td>
                          <td>{county.county_seat || '-'}</td>
                          <td>{county.sale_runner ? labelFor(SALE_RUNNERS, county.sale_runner) : '-'}</td>
                          <td>
                            <span className={`badge ${county.sale_count > 0 ? 'badge-info' : 'badge-secondary'}`}>
                              {county.sale_count}
                            </span>
                          </td>
                          <td>
                            {county.verified_at
                              ? <span className="badge badge-success">{new Date(county.verified_at).toLocaleDateString()}</span>
                              : <span className="badge badge-secondary">Unverified</span>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-secondary" onClick={() => startEditCounty(county.id)}>Edit</button>
                            <button
                              className="btn btn-secondary"
                              style={{ marginLeft: '6px' }}
                              onClick={() => stampCounty(county.id, Boolean(county.verified_at))}
                            >
                              {county.verified_at ? 'Unverify' : 'Verify'}
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ marginLeft: '6px' }}
                              onClick={() => openCounty(county)}
                            >
                              {expandedCounty === county.id ? 'Hide sales' : 'Sales'}
                            </button>
                            <RowSaveFlag
                              show={Boolean(savedFlags[`county:${county.id}`])}
                              label={countyLabel(county.county_name)}
                            />
                          </td>
                        </tr>

                        {editingCounty === county.id && (
                          <tr>
                            <td colSpan={7}>
                              <div style={panelStyle}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                                  {[
                                    ['county_name', 'County name'],
                                    ['county_seat', 'County seat'],
                                    ['sale_website_url', 'Sale website'],
                                    ['contact_phone', 'Phone'],
                                    ['contact_email', 'Email']
                                  ].map(([field, label]) => (
                                    <div key={field}>
                                      <label style={cellLabelStyle} htmlFor={`${field}-${county.id}`}>{label}</label>
                                      <input
                                        id={`${field}-${county.id}`}
                                        style={inputStyle}
                                        value={countyDraft[field]}
                                        onChange={e => setCountyDraft({ ...countyDraft, [field]: e.target.value })}
                                        onKeyDown={keyHandler(() => saveCounty(county.id), () => setEditingCounty(null))}
                                      />
                                    </div>
                                  ))}
                                  <div>
                                    <label style={cellLabelStyle} htmlFor={`runner-${county.id}`}>Runs the sale</label>
                                    <select
                                      id={`runner-${county.id}`}
                                      style={inputStyle}
                                      value={countyDraft.sale_runner}
                                      onChange={e => setCountyDraft({ ...countyDraft, sale_runner: e.target.value })}
                                    >
                                      {SALE_RUNNERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                  </div>
                                </div>
                                <div style={{ marginTop: '12px' }}>
                                  <label style={cellLabelStyle} htmlFor={`county-notes-${county.id}`}>Notes</label>
                                  <textarea
                                    id={`county-notes-${county.id}`}
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                    value={countyDraft.notes}
                                    onChange={e => setCountyDraft({ ...countyDraft, notes: e.target.value })}
                                  />
                                </div>
                                <div style={{ marginTop: '12px' }}>
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => saveCounty(county.id)}
                                    disabled={savingCounty}
                                  >
                                    {savingCounty ? 'Saving' : 'Save'}
                                  </button>
                                  <button
                                    className="btn btn-secondary"
                                    style={{ marginLeft: '8px' }}
                                    onClick={() => setEditingCounty(null)}
                                    disabled={savingCounty}
                                  >
                                    Cancel
                                  </button>
                                  <FormMessage message={countyFormMessage} innerRef={countyMessageRef} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {expandedCounty === county.id && (
                          <tr>
                            <td colSpan={7}>
                              <div style={panelStyle}>
                                <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>
                                  Sales for {countyLabel(county.county_name)}
                                </h3>

                                {sales.length === 0 && (
                                  <p style={{ color: 'var(--color-text-light)' }}>
                                    No sales entered yet for this county.
                                  </p>
                                )}

                                {sales.map(sale => (
                                  <div
                                    key={sale.id}
                                    style={{
                                      borderTop: '1px solid var(--color-border)',
                                      paddingTop: '10px',
                                      marginTop: '10px'
                                    }}
                                  >
                                    {editingSale === sale.id ? (
                                      <SaleForm
                                        draft={saleDraft}
                                        setDraft={setSaleDraft}
                                        inputStyle={inputStyle}
                                        cellLabelStyle={cellLabelStyle}
                                        idPrefix={`sale-${sale.id}`}
                                        onSave={() => saveSale(sale.id)}
                                        onCancel={() => setEditingSale(null)}
                                        saveLabel="Save sale"
                                        saving={savingSale}
                                        message={saleFormMessage}
                                        messageRef={saleMessageRef}
                                      />
                                    ) : (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                        <div>
                                          <span className={`badge ${STATUS_BADGE[sale.status] || 'badge-secondary'}`}>
                                            {labelFor(SALE_STATUSES, sale.status)}
                                          </span>
                                          <strong style={{ marginLeft: '8px', color: 'var(--color-text)' }}>
                                            {labelFor(SALE_KINDS, sale.sale_kind)}
                                          </strong>
                                          <span style={{ marginLeft: '8px', color: 'var(--color-text-light)' }}>
                                            {sale.platform ? labelFor(PLATFORMS, sale.platform) : 'Platform not set'}
                                          </span>
                                          <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginTop: '4px' }}>
                                            {sale.next_sale_date
                                              ? `Next sale ${toDateInput(sale.next_sale_date)}`
                                              : (sale.typical_month
                                                ? `Typically ${MONTHS[sale.typical_month]}, date not yet posted`
                                                : 'No date and no typical month recorded')}
                                            {sale.registration_deadline && `, registration closes ${toDateInput(sale.registration_deadline)}`}
                                          </div>
                                          {sale.list_url && (
                                            <div style={{ fontSize: '0.9rem', marginTop: '4px' }}>
                                              <a href={sale.list_url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand-text)' }}>
                                                List
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                        <div style={{ whiteSpace: 'nowrap' }}>
                                          <button className="btn btn-secondary" onClick={() => startEditSale(sale)}>Edit</button>
                                          <button
                                            className="btn btn-secondary"
                                            style={{ marginLeft: '6px' }}
                                            onClick={() => loadHistory(sale.id)}
                                          >
                                            {history.saleId === sale.id ? 'Hide history' : 'History'}
                                          </button>
                                          <RowSaveFlag
                                            show={Boolean(savedFlags[`sale:${sale.id}`])}
                                            label={labelFor(SALE_KINDS, sale.sale_kind)}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {history.saleId === sale.id && (
                                      <div style={{ marginTop: '10px', fontSize: '0.85rem' }}>
                                        {history.rows.length === 0 && (
                                          <p style={{ color: 'var(--color-text-muted)' }}>No changes logged yet.</p>
                                        )}
                                        {history.rows.map(row => (
                                          <div key={row.id} style={{ color: 'var(--color-text-light)', padding: '2px 0' }}>
                                            {new Date(row.changed_at).toLocaleString()}
                                            {': '}
                                            <strong style={{ color: 'var(--color-text)' }}>{row.field}</strong>
                                            {row.field === 'created'
                                              ? ' created'
                                              : ` changed from ${row.old_value === null ? 'empty' : row.old_value} to ${row.new_value === null ? 'empty' : row.new_value}`}
                                            {row.changed_by_name && ` by ${row.changed_by_name}`}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}

                                <div style={{ marginTop: '14px' }}>
                                  {addingSale ? (
                                    <SaleForm
                                      draft={saleDraft}
                                      setDraft={setSaleDraft}
                                      inputStyle={inputStyle}
                                      cellLabelStyle={cellLabelStyle}
                                      idPrefix={`new-sale-${county.id}`}
                                      onSave={createSale}
                                      onCancel={() => setAddingSale(false)}
                                      saveLabel="Add sale"
                                      saving={savingSale}
                                      message={saleFormMessage}
                                      messageRef={saleMessageRef}
                                    />
                                  ) : (
                                    <button className="btn btn-primary" onClick={startAddSale}>Add a sale</button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* --------------------------------------------------- attention */}
        {!loading && view === 'attention' && (
          <div className="card">
            <div style={{ marginBottom: '16px', maxWidth: '280px' }}>
              <label style={cellLabelStyle} htmlFor="attention-state">Filter by state</label>
              <select
                id="attention-state"
                style={inputStyle}
                value={attentionState}
                onChange={e => setAttentionState(e.target.value)}
              >
                <option value="">All states</option>
                {states.map(s => (
                  <option key={s.state_code} value={s.state_code}>{s.state_code} {s.name}</option>
                ))}
              </select>
            </div>

            {!attention && <p style={{ color: 'var(--color-text-light)' }}>Loading.</p>}

            {attention && (
              <>
                {/* FIRST, above the stale dates, because this is the only list
                    on the page with a person waiting at the other end of it. */}
                <h3 style={{ color: 'var(--color-heading)' }}>
                  Reported problems ({(attention.problemReports || []).length})
                </h3>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                  Sent by subscribers from the Report a problem link. Each one was
                  emailed to you as well. Resolving it takes it off this list and
                  cannot be undone.
                </p>
                <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>From</th>
                        <th>Where</th>
                        <th>What they said</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(attention.problemReports || []).length === 0 && (
                        <tr><td colSpan={5} style={{ color: 'var(--color-text-light)' }}>
                          No open reports.
                        </td></tr>
                      )}
                      {(attention.problemReports || []).map(report => (
                        <tr key={report.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {new Date(report.created_at).toLocaleDateString()}
                          </td>
                          <td>{report.subscriber_email}</td>
                          <td>
                            {report.county_id ? (
                              <button
                                className="btn btn-secondary"
                                onClick={() => {
                                  setSelectedState(report.county_state_code);
                                  setCountySearch(report.county_name);
                                  setView('counties');
                                }}
                              >
                                {countyLabel(report.county_name)}, {report.county_state_code}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)' }}>
                                {report.state_code || report.page}
                              </span>
                            )}
                          </td>
                          {/* Wrapped rather than truncated. The message is the
                              only part of a report that cannot be reconstructed
                              from anywhere else on this row. */}
                          <td style={{ whiteSpace: 'pre-wrap', maxWidth: '380px' }}>
                            {report.message}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              disabled={resolving === report.id}
                              onClick={() => resolveReport(report.id)}
                            >
                              {resolving === report.id ? 'Resolving' : 'Resolve'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Second, under the reports and above the stale dates. A
                    state in here is one a paying subscriber can open today and
                    find nothing in, which is the Arizona case: set Live while
                    it was still empty. Deliberately NOT added to the tab badge,
                    which counts open problem reports and nothing else. */}
                <h3 style={{ color: 'var(--color-heading)' }}>
                  Live states with nothing to show ({(attention.liveWithNothingToShow || []).length})
                </h3>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                  These states are Live, so subscribers see them in the feed. Each one
                  is either unverified, or has no sale with a date still ahead of it and
                  no typical month, so there is nothing for the feed to show.
                </p>
                <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>State</th>
                        <th>What is missing</th>
                        <th>Counties with something to show</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(attention.liveWithNothingToShow || []).length === 0 && (
                        <tr><td colSpan={4} style={{ color: 'var(--color-text-light)' }}>
                          Every live state has something to show.
                        </td></tr>
                      )}
                      {(attention.liveWithNothingToShow || []).map(row => (
                        <tr key={row.state_code}>
                          <td style={{ fontWeight: 600 }}>{row.state_code} {row.name}</td>
                          <td>{row.problem}</td>
                          <td>{row.counties_with_sales} of {row.county_count}</td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setFocusStateCode(row.state_code);
                                setView('states');
                              }}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 style={{ color: 'var(--color-heading)' }}>
                  Date has passed ({(attention.datePassed || []).length})
                </h3>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                  The entered sale date is behind us. Enter the next one. Until it is
                  entered, a sale with a typical month reads as "Typically September"
                  on the subscriber calendar, and one without a typical month is not
                  shown there at all.
                </p>
                <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>State</th>
                        <th>County</th>
                        <th>Kind</th>
                        <th>Status</th>
                        <th>Date that passed</th>
                        <th>Typical month</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(attention.datePassed || []).length === 0 && (
                        <tr><td colSpan={7} style={{ color: 'var(--color-text-light)' }}>No sale date has passed.</td></tr>
                      )}
                      {(attention.datePassed || []).map(sale => (
                        <tr key={sale.id}>
                          <td>{sale.state_code}</td>
                          <td style={{ fontWeight: 600 }}>{countyLabel(sale.county_name)}</td>
                          <td>{labelFor(SALE_KINDS, sale.sale_kind)}</td>
                          <td>
                            <span className={`badge ${STATUS_BADGE[sale.status] || 'badge-secondary'}`}>
                              {labelFor(SALE_STATUSES, sale.status)}
                            </span>
                          </td>
                          <td>{toDateInput(sale.next_sale_date)}</td>
                          <td>
                            {sale.typical_month_label || (
                              <span style={{ color: 'var(--color-text-muted)' }}>Not set</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setSelectedState(sale.state_code);
                                setCountySearch(sale.county_name);
                                setView('counties');
                              }}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 style={{ color: 'var(--color-heading)' }}>
                  Soft or imminent sales ({attention.sales.length})
                </h3>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                  Status expected or unknown, with a sale date inside {attention.windowDays} days
                  or no date at all.
                </p>
                <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>State</th>
                        <th>County</th>
                        <th>Kind</th>
                        <th>Status</th>
                        <th>Next sale</th>
                        <th>Registration</th>
                        <th>Source last checked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attention.sales.length === 0 && (
                        <tr><td colSpan={7} style={{ color: 'var(--color-text-light)' }}>Nothing needs attention here.</td></tr>
                      )}
                      {attention.sales.map(sale => (
                        <tr key={sale.id}>
                          <td>{sale.state_code}</td>
                          <td style={{ fontWeight: 600 }}>{countyLabel(sale.county_name)}</td>
                          <td>{labelFor(SALE_KINDS, sale.sale_kind)}</td>
                          <td>
                            <span className={`badge ${STATUS_BADGE[sale.status] || 'badge-secondary'}`}>
                              {labelFor(SALE_STATUSES, sale.status)}
                            </span>
                          </td>
                          <td>{sale.next_sale_date ? toDateInput(sale.next_sale_date) : 'Not posted'}</td>
                          <td>{sale.registration_deadline ? toDateInput(sale.registration_deadline) : '-'}</td>
                          <td>{sale.source_last_checked_at ? new Date(sale.source_last_checked_at).toLocaleDateString() : 'Never'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 style={{ color: 'var(--color-heading)' }}>
                  Counties with no sales at all ({attention.countiesWithNoSales.length})
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>State</th>
                        <th>County</th>
                        <th>FIPS</th>
                        <th>Stamp</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attention.countiesWithNoSales.length === 0 && (
                        <tr><td colSpan={5} style={{ color: 'var(--color-text-light)' }}>Every county has at least one sale.</td></tr>
                      )}
                      {attention.countiesWithNoSales.map(county => (
                        <tr key={county.id}>
                          <td>{county.state_code}</td>
                          <td style={{ fontWeight: 600 }}>{countyLabel(county.county_name)}</td>
                          <td style={{ fontFamily: 'monospace' }}>{county.fips_code}</td>
                          <td>
                            {county.verified_at
                              ? <span className="badge badge-success">Verified</span>
                              : <span className="badge badge-secondary">Unverified</span>}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setSelectedState(county.state_code);
                                setCountySearch(county.county_name);
                                setView('counties');
                              }}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* -------------------------------------------------- bulk import */}
        {!loading && view === 'bulk' && (
          <CalendarBulkImport
            api={api}
            apiBase={process.env.REACT_APP_API_URL}
            token={token}
            inputStyle={inputStyle}
            cellLabelStyle={cellLabelStyle}
            panelStyle={panelStyle}
            onFlash={flash}
            onImported={reloadAfterImport}
            onOpenCounty={(stateCode, countyName) => {
              setSelectedState(stateCode);
              setCountySearch(countyName);
              setView('counties');
            }}
            onOpenCounties={() => setView('counties')}
            onOpenState={(stateCode) => {
              setFocusStateCode(stateCode);
              setView('states');
            }}
          />
        )}

        {/* --------------------------------------------------- subscribers */}
        {!loading && view === 'subscribers' && (
          <CalendarSubscribers
            api={api}
            inputStyle={inputStyle}
            cellLabelStyle={cellLabelStyle}
            panelStyle={panelStyle}
            onFlash={flash}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The add and edit form for a sale. One component for both, so a field added to
 * an edit cannot be missing from the add path, and so a refusal behaves the
 * same whichever one the owner is in.
 *
 * `message` is the outcome of the last save, shown under these buttons the way
 * the state and county panels show theirs. It is the error path that needs it:
 * this form used to close on a refusal and leave the reason in a banner at the
 * top of the page, which lost the typed row and explained nothing where the
 * owner was looking.
 */
function SaleForm({
  draft, setDraft, inputStyle, cellLabelStyle, idPrefix,
  onSave, onCancel, saveLabel, saving, message, messageRef
}) {
  const set = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px' }}>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-kind`}>Kind</label>
          <select id={`${idPrefix}-kind`} style={inputStyle} value={draft.sale_kind} onChange={set('sale_kind')}>
            {SALE_KINDS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-platform`}>Platform</label>
          <select id={`${idPrefix}-platform`} style={inputStyle} value={draft.platform} onChange={set('platform')}>
            {PLATFORMS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-status`}>Status</label>
          <select id={`${idPrefix}-status`} style={inputStyle} value={draft.status} onChange={set('status')}>
            {SALE_STATUSES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-month`}>Typical month</label>
          <select id={`${idPrefix}-month`} style={inputStyle} value={draft.typical_month} onChange={set('typical_month')}>
            <option value="">Not set</option>
            {MONTHS.slice(1).map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-date`}>Next sale date</label>
          <input
            id={`${idPrefix}-date`}
            type="date"
            style={inputStyle}
            value={draft.next_sale_date}
            onChange={set('next_sale_date')}
            onKeyDown={onKeyDown}
          />
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-registration`}>Registration deadline</label>
          <input
            id={`${idPrefix}-registration`}
            type="date"
            style={inputStyle}
            value={draft.registration_deadline}
            onChange={set('registration_deadline')}
            onKeyDown={onKeyDown}
          />
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-list`}>List URL</label>
          <input
            id={`${idPrefix}-list`}
            style={inputStyle}
            value={draft.list_url}
            onChange={set('list_url')}
            onKeyDown={onKeyDown}
          />
        </div>
        <div>
          <label style={cellLabelStyle} htmlFor={`${idPrefix}-source`}>Source URL (required)</label>
          <input
            id={`${idPrefix}-source`}
            style={inputStyle}
            value={draft.source_url}
            onChange={set('source_url')}
            onKeyDown={onKeyDown}
            placeholder="Where this information came from"
          />
        </div>
      </div>

      <div style={{ marginTop: '12px' }}>
        <label style={cellLabelStyle} htmlFor={`${idPrefix}-notes`}>Notes</label>
        <textarea
          id={`${idPrefix}-notes`}
          style={{ ...inputStyle, minHeight: '54px', resize: 'vertical' }}
          value={draft.notes}
          onChange={set('notes')}
        />
      </div>

      <div style={{ marginTop: '12px' }}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving' : saveLabel}
        </button>
        <button
          className="btn btn-secondary"
          style={{ marginLeft: '8px' }}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <span style={{ marginLeft: '12px', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
          Enter saves. Escape cancels. Adding keeps the source URL for the next row.
        </span>
        <FormMessage message={message} innerRef={messageRef} />
      </div>
    </div>
  );
}

export default MasterAdminCalendar;
